/**
 * Your cards: a copy of onlyfans.com/my/payments (bundle chunks payments*.js,
 * store module `payments` / `transactions`, captured 2026-10-22). Parsers for
 * OnlyFans' answers and the exact requests its store actions make.
 */
import type { QueryValue } from '../../ofApi'

export const OF_PAYMENTS = 'https://onlyfans.com/my/payments'
export const OF_ADD_CARD = `${OF_PAYMENTS}/add_card`
export const OF_ALTERNATIVE = `${OF_PAYMENTS}/alternative`
export const OF_ALTERNATIVE_ADD = `${OF_PAYMENTS}/alternative/add`
export const OF_TRANSACTIONS = `${OF_PAYMENTS}/list`
export const OF_VERIFICATION = 'https://onlyfans.com/my/verification'
export const OF_CONTACT = 'https://onlyfans.com/contact'

type Raw = Record<string, unknown>
const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {})
const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v))
const num = (v: unknown, fallback = 0) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN
  return Number.isFinite(n) ? n : fallback
}

// ── Tabs ──────────────────────────────────────────────────────────────────

/** OnlyFans' routes: /my/payments (Payments), /my/payments/list, /my/payments/alternative. */
export type TabId = 'cards' | 'list' | 'alternative'

/** The transactions list's filter pills (`?type=`). */
export type TxType = 'all' | 'error' | 'credit' | 'payment'
export const TX_TYPES: readonly TxType[] = ['all', 'error', 'credit', 'payment']

/** The period picker's kinds (OnlyFans' J5). */
export type PeriodType = '24' | 'day' | 'month' | 'year' | 'all' | 'custom'

export interface PageQuery {
  tab: TabId
  /** `type` on the list (absent = OnlyFans' default "all"). */
  type: TxType | null
  dateType: PeriodType | null
  dateFrom: string
  dateTo: string
}

export function parseQuery(query: string): PageQuery {
  const q = new URLSearchParams(query)
  const tab = q.get('tab')
  const type = q.get('type') as TxType | null
  const dateType = q.get('dateType') as PeriodType | null
  return {
    tab: tab === 'list' || tab === 'alternative' ? tab : 'cards',
    type: type && TX_TYPES.includes(type) ? type : null,
    dateType: dateType && ['24', 'day', 'month', 'year', 'custom'].includes(dateType) ? dateType : null,
    dateFrom: q.get('dateFrom') ?? '',
    dateTo: q.get('dateTo') ?? '',
  }
}

/** Back to a query string, keys in OnlyFans' order; "all time" drops the dates (TransactionsDatepicker). */
export function buildQuery(p: Partial<PageQuery>): string {
  const q = new URLSearchParams()
  if (p.tab && p.tab !== 'cards') q.set('tab', p.tab)
  if (p.tab === 'list') {
    if (p.type) q.set('type', p.type)
    if (p.dateType && p.dateType !== 'all' && p.dateFrom) {
      q.set('dateType', p.dateType)
      if (p.dateTo) q.set('dateTo', p.dateTo)
      q.set('dateFrom', p.dateFrom)
    }
  }
  return q.toString()
}

export const refererFor = (tab: TabId) => (tab === 'list' ? OF_TRANSACTIONS : tab === 'alternative' ? OF_ALTERNATIVE : OF_PAYMENTS)

// ── users/me (the wallet's fields) ─────────────────────────────────────────

export interface WalletUser {
  creditBalance: number
  creditsMax: number
  creditsMin: number
  creditsMinAlternatives: number
  isCreditsEnabled: boolean
  canAlternativeWalletTopUp: boolean
  /** Undefined on OnlyFans = the toggle isn't offered. */
  walletFirstRebills: boolean | undefined
  isWalletAutorecharge: boolean
  walletAutorechargeAmount: number
  walletAutorechargeMin: number
  isRealCardConnected: boolean
  canAddCard: boolean
  isPaymentCardConnected: boolean
  isDeleteInitiated: boolean
  requireCardVerification: boolean
  ageVerificationRequired: boolean
  isAgeVerified: boolean
  needIVApprove: boolean
  ivStatus: string
  /** ageVerificationSession.status: pending / failed / … */
  ageVerificationStatus: string
  isEmailRequired: boolean
  isEmailChecked: boolean
  isOtpEnabled: boolean
  /** The day the creator joined (or first posted): where "All time" starts. */
  startDate: string
}

export function toWalletUser(raw: unknown): WalletUser {
  const u = obj(raw)
  return {
    creditBalance: num(u.creditBalance),
    creditsMax: num(u.creditsMax, 200),
    creditsMin: num(u.creditsMin, 10),
    creditsMinAlternatives: num(u.creditsMinAlternatives, 5),
    isCreditsEnabled: u.isCreditsEnabled === true,
    canAlternativeWalletTopUp: u.canAlternativeWalletTopUp === true,
    walletFirstRebills: typeof u.walletFirstRebills === 'boolean' ? u.walletFirstRebills : undefined,
    isWalletAutorecharge: u.isWalletAutorecharge === true,
    walletAutorechargeAmount: num(u.walletAutorechargeAmount),
    walletAutorechargeMin: num(u.walletAutorechargeMin),
    isRealCardConnected: u.isRealCardConnected === true,
    canAddCard: u.canAddCard === true,
    isPaymentCardConnected: u.isPaymentCardConnected === true,
    isDeleteInitiated: u.isDeleteInitiated === true,
    requireCardVerification: u.requireCardVerification === true,
    ageVerificationRequired: u.ageVerificationRequired === true,
    isAgeVerified: u.isAgeVerified === true,
    needIVApprove: u.needIVApprove === true,
    ivStatus: str(u.ivStatus),
    ageVerificationStatus: str(obj(u.ageVerificationSession).status),
    isEmailRequired: u.isEmailRequired === true,
    isEmailChecked: u.isEmailChecked !== false,
    isOtpEnabled: u.isOtpEnabled === true,
    startDate: str(u.joinDate) || str(u.firstPublishedPostDate),
  }
}

/** OnlyFans shows the Alternative methods tab (and loads them) only for these users. */
export const canAlternative = (u: WalletUser) => u.isCreditsEnabled && u.canAlternativeWalletTopUp

/** PaymentsPage.hasPaymentsEmail: OnlyFans asks to confirm the email before showing cards. */
export const needsPaymentsEmail = (u: WalletUser) => u.isEmailRequired && (!u.isOtpEnabled || !u.isEmailChecked)

// ── Cards (GET payments/cards) ─────────────────────────────────────────────

export type Verification = 'verified' | 'pending' | 'unverified' | 'unverifiable' | string

export interface PaymentCard {
  id: number
  brand: string
  last4: string
  isDefault: boolean
  canPay: boolean
  isExpired: boolean
  expireDate: string
  verificationStatus: Verification
  lastError: string
  addressCountry: string
}

export function toCard(raw: unknown): PaymentCard | null {
  const c = obj(raw)
  const id = num(c.id, NaN)
  if (!Number.isFinite(id)) return null
  return {
    id,
    brand: str(c.brand),
    last4: str(c.last4),
    isDefault: c.isDefault === true,
    canPay: c.canPay !== false,
    isExpired: c.isExpired === true,
    expireDate: str(c.expireDate),
    verificationStatus: str(c.verificationStatus),
    lastError: str(c.lastError),
    addressCountry: str(c.addressCountry),
  }
}

export const toCards = (raw: unknown): PaymentCard[] => (Array.isArray(raw) ? raw.map(toCard).filter((c): c is PaymentCard => !!c) : [])

/** PaymentsCard.isActiveCard: Make default / Edit address are offered only for these. */
export const isActiveCard = (c: PaymentCard) => !c.isExpired && c.canPay

/** PaymentsCard.canVerifyCard. */
export const canVerifyCard = (c: PaymentCard, u: WalletUser) =>
  u.requireCardVerification && c.verificationStatus !== 'verified' && c.verificationStatus !== 'unverifiable'

/** The status line's i18n key (debit_card_verification_status_*). */
export const verificationKey = (c: PaymentCard, u: WalletUser) =>
  `verification.${u.requireCardVerification ? c.verificationStatus || 'unverified' : 'active'}`

/** Red status text: can't pay or unverified, when OnlyFans requires verification. */
export const isStatusDanger = (c: PaymentCard, u: WalletUser) => (!c.canPay || c.verificationStatus !== 'verified') && u.requireCardVerification

/** Cards.vue shows three, then "View all". */
export const MAX_COLLAPSE = 3

/** The store's setCardDefault mutation. */
export const withDefault = (cards: PaymentCard[], id: number) => cards.map((c) => ({ ...c, isDefault: c.id === id }))

// ── Alternative methods (GET alternative-payment-methods) ──────────────────

export interface AltParameter {
  key: string
  value: string
}

export interface AlternativeMethod {
  id: number
  title: string
  country: string
  currency: string
  currencyRate: number
  currencyRateToUsd: number
  brand: string
  /** First/last name, birth date and the method's own parameters, empty ones left out. */
  parameters: AltParameter[]
}

export function toAlternative(raw: unknown): AlternativeMethod | null {
  const m = obj(raw)
  const id = num(m.id, NaN)
  if (!Number.isFinite(id)) return null
  const own = Array.isArray(m.parameters) ? m.parameters.map(obj).map((p) => ({ key: str(p.key || p.name), value: str(p.value) })) : []
  const parameters = [
    { key: 'bank_legal_first_name_title', value: str(m.firstName) },
    { key: 'bank_legal_last_name_title', value: str(m.lastName) },
    { key: 'bank_date_of_birth_title', value: str(m.dob) },
    ...own,
  ].filter((p) => !!p.value)
  return {
    id,
    title: str(m.title),
    country: str(m.country),
    currency: str(m.currency) || 'USD',
    currencyRate: num(m.currencyRate, 1) || 1,
    currencyRateToUsd: num(m.currencyRateToUsd, 1) || 1,
    brand: str(m.brand),
    parameters,
  }
}

export const toAlternatives = (raw: unknown): AlternativeMethod[] =>
  Array.isArray(raw) ? raw.map(toAlternative).filter((m): m is AlternativeMethod => !!m) : []

/** The tab and list title is "Pix" for a Brazilian Pix method (PaymentsMenu). */
export const isPix = (methods: AlternativeMethod[]) => methods[0]?.country === 'BR' && methods[0]?.title === 'Pix'

/** PaymentsAlternativeItem's limits, in the method's currency. */
export function alternativeLimits(m: AlternativeMethod, u: WalletUser) {
  const min = +(u.creditsMinAlternatives * m.currencyRate).toFixed(2)
  const max = +Math.max(min, (u.creditsMax - u.creditBalance) * m.currencyRate).toFixed(2)
  return { min, max }
}

/** PaymentsAlternativeItem.inputErrorMessage: an i18n key and its values, or null. */
export function alternativeAmountError(amount: number | null, m: AlternativeMethod, u: WalletUser): { key: string; values?: Record<string, string> } | null {
  if (!amount) return null
  const { min, max } = alternativeLimits(m, u)
  if (u.creditBalance >= u.creditsMax) return { key: 'maxBalance' }
  if (amount < min) return { key: 'minTopUp', values: { amount: min.toFixed(2), currency: m.currency } }
  if (amount > max) return { key: 'maxTopUp', values: { amount: max.toFixed(2), currency: m.currency } }
  return null
}

/** "BRL 50 = USD 9.50" under the amount (none for USD methods). */
export function convertorMessage(amount: number | null, m: AlternativeMethod, intl: string): string {
  if (m.currency === 'USD') return ''
  const a = amount || 1
  const usd = (a * m.currencyRateToUsd).toFixed(2)
  const fmt = (v: number | string, currency: string) =>
    new Intl.NumberFormat(intl, { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(v))
  try {
    return `${fmt(a, m.currency)} = ${fmt(usd, 'USD')}`
  } catch {
    return ''
  }
}

// ── Wallet ────────────────────────────────────────────────────────────────

/** amountHumanizeShort: "$0", "$12.5" → "$12.50", "$10". */
export const moneyShort = (v: number) => `$${(Number.isFinite(v) ? v : 0).toFixed(2).replace('.00', '')}`

/** OnlyFans' WalletTopUp presets (pass-through mode on this page). */
export const TOP_UP_PRESETS = [10, 20, 50, 100] as const

export interface Preset {
  id: number
  amount: number
  disabled: boolean
}

/**
 * WalletTopUp.preparedPresetAmounts: each preset shrinks to what still fits
 * under the wallet's maximum, and is disabled when it can't fit at all.
 */
export function topUpPresets(u: WalletUser, presets: readonly number[] = TOP_UP_PRESETS): Preset[] {
  const fits = (v: number) => u.creditBalance + v <= u.creditsMax
  return presets.map((s, i) => {
    const prev = presets[i - 1]
    const room = Math.round(100 * Math.min(s, u.creditsMax - u.creditBalance)) / 100
    // OnlyFans compares with the previous preset, so the first one never shrinks.
    const shrinks = room >= 1 && prev !== undefined && room > prev
    const prevBlocked = prev !== undefined && !fits(prev)
    const amount = shrinks && !prevBlocked ? room : s
    return { id: s, amount, disabled: !fits(amount) || u.isDeleteInitiated }
  })
}

/** WalletCredits: auto-recharge selects. */
export const RECHARGE_AMOUNTS = [10, 20, 30, 40, 50] as const
export const RECHARGE_MINS = [1, 5, 10, 20, 30, 40, 50] as const
export const INITIAL_RECHARGE = 10
export const INITIAL_RECHARGE_MIN = 5

/** Wallet options are shown when OnlyFans sent walletFirstRebills, or a real card is connected. */
export const hasWalletOptions = (u: WalletUser) => u.walletFirstRebills !== undefined || u.isRealCardConnected

// ── Transactions (GET payments/all/transactions) ──────────────────────────

/** OnlyFans' default page size (api Jg). */
export const TX_PAGE = 10
export const LATEST_LIMIT = 5

export interface Transaction {
  id: string
  createdAt: string
  amount: number
  status: string
  source: string
  /** "···· ···· ···· 1234", the alternative method's name, or '' for the wallet. */
  identifier: string
  brand: string
  description: string
  userInactive: boolean
  vat: { name: string; amount: number; percentage: number }
  tax: { amount: number; percentage: number }
  mediaTax: { amount: number; percentage: number }
  retryType: string
}

export function toTransaction(raw: unknown, i = 0): Transaction {
  const t = obj(raw)
  const card = obj(t.card)
  const alt = obj(t.alternative)
  const user = obj(t.user)
  const source = str(t.source)
  const last4 = str(card.last4)
  return {
    id: str(t.id) || `${str(t.createdAt)}-${i}`,
    createdAt: str(t.createdAt),
    amount: num(t.amount),
    status: str(t.status),
    source,
    identifier: source === 'card' ? (last4 ? `···· ···· ···· ${last4}` : '') : source === 'alternative' ? str(alt.name) : '',
    brand: source === 'alternative' ? str(alt.brand) : source === 'card' ? str(card.brand) : '',
    description: str(t.description),
    userInactive: t.user !== undefined && t.user !== null && (user.isDeleted === true || user.isActive !== true),
    vat: { name: str(t.vatName), amount: num(t.vatAmount), percentage: num(t.vatPercentage) },
    tax: { amount: num(t.taxAmount), percentage: num(t.taxPercentage) },
    mediaTax: { amount: num(t.mediaTaxAmount), percentage: num(t.mediaTaxPercentage) },
    retryType: str(obj(t.retry).type),
  }
}

export interface TxPage {
  list: Transaction[]
  nextMarker: string | number | null
  hasMore: boolean
}

export function toTxPage(raw: unknown): TxPage {
  const p = obj(raw)
  return {
    list: Array.isArray(p.list) ? p.list.map(toTransaction) : [],
    nextMarker: typeof p.nextMarker === 'string' || typeof p.nextMarker === 'number' ? p.nextMarker : null,
    hasMore: p.hasMore === true,
  }
}

const sum = (values: number[]) => values.reduce((a, b) => Math.round(100 * (a + b)) / 100, 0)

/**
 * The amount column: the sum with VAT and taxes ("$12.00") and, when any
 * applies, "(20% VAT + 5% Tax)" — OnlyFans' useTransactionTaxes.
 */
export function txAmount(tx: Transaction): { amount: string; taxes: string } {
  const positive = tx.amount > 0
  const parts: string[] = []
  if (positive && tx.vat.amount > 0) parts.push(`${tx.vat.percentage}% ${tx.vat.name}`)
  const taxes = [tx.tax, tx.mediaTax].filter((x) => positive && x.amount > 0)
  if (taxes.length) parts.push(taxes.length > 1 ? 'Tax' : `${sum([tx.tax.percentage, tx.mediaTax.percentage])}% Tax`)
  const total = sum([tx.amount, tx.vat.amount, sum([tx.tax.amount, tx.mediaTax.amount])])
  return { amount: `$${total.toFixed(2)}`, taxes: parts.join(' + ') }
}

const DAY24_LOCALES: Partial<Record<string, string>> = { ru: 'ru-RU', uk: 'uk-UA', es: 'es-ES' }

/** "Oct 22, 2026, 9:05 am" (OnlyFans' UtilsTime type "payments"); 24h day-first for ru/uk/es. */
export function txDate(iso: string, lang: string): string {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return ''
  const day24Locale = DAY24_LOCALES[lang]
  const day24 = day24Locale !== undefined
  const mon = d.toLocaleString(day24Locale ?? 'en-US', { month: 'short' }).replace('.', '')
  const h = d.getHours()
  const mm = String(d.getMinutes()).padStart(2, '0')
  if (day24) return `${d.getDate()} ${mon}, ${d.getFullYear()}, ${h}:${mm}`
  return `${mon} ${d.getDate()}, ${d.getFullYear()}, ${h % 12 || 12}:${mm} ${h < 12 ? 'am' : 'pm'}`
}

/** payments/all/transactions query, OnlyFans' key order (limit, marker, type, date_from, date_to). */
export function txQuery(o: { limit?: number; marker?: string | number | null; type?: TxType | null; dateFrom?: string; dateTo?: string }): Record<string, QueryValue> {
  return {
    limit: o.limit ?? TX_PAGE,
    marker: o.marker ?? undefined,
    type: o.type || 'all',
    date_from: o.dateFrom || undefined,
    date_to: o.dateTo || undefined,
  }
}

// ── Period (TransactionsDatepicker / PeriodDatepicker) ─────────────────────

const DAY = 86_400_000

/** dayjs().format(): local time with its offset, "2026-10-22T09:05:00+03:00". */
export function isoLocal(d: Date): string {
  const p = (n: number) => String(Math.trunc(Math.abs(n))).padStart(2, '0')
  const off = -d.getTimezoneOffset()
  const sign = off >= 0 ? '+' : '-'
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}${sign}${p(off / 60)}:${p(off % 60)}`
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)

/** Where "All time" starts: the join date, else two years back (getter userStartDate). */
export function userStart(u: Pick<WalletUser, 'startDate'>, now = new Date()): Date {
  const d = new Date(u.startDate)
  return Number.isFinite(d.getTime()) ? d : new Date(now.getFullYear() - 2, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes())
}

export interface Period {
  type: PeriodType
  start: Date
  end: Date
  /** "Last N days": N. */
  days: number | null
}

/** OnlyFans' setDate (v6 + setTimePeriod): the range for a picked period, clamped to [start, now]. */
export function periodRange(type: PeriodType, value: number | { start: string; end: string } | null, start: Date, now = new Date()): Period {
  let s: Date
  let e: Date
  const v = typeof value === 'number' ? value : 0
  switch (type) {
    case '24':
      s = new Date(now.getTime() - DAY)
      e = now
      break
    case 'day':
      s = startOfDay(new Date(now.getTime() - v * DAY))
      e = now
      break
    case 'month':
      s = new Date(now.getFullYear(), now.getMonth() - v, 1)
      e = v === 0 ? endOfDay(now) : endOfDay(new Date(now.getFullYear(), now.getMonth() - v + 1, 0))
      break
    case 'year':
      s = new Date(v, 0, 1)
      e = now.getFullYear() === v ? endOfDay(now) : endOfDay(new Date(v, 11, 31))
      break
    case 'custom': {
      const r = value && typeof value === 'object' ? value : { start: '', end: '' }
      s = startOfDay(new Date(`${r.start}T00:00:00`))
      e = endOfDay(new Date(`${r.end}T00:00:00`))
      break
    }
    default:
      s = start
      e = now
  }
  if (s < start) s = start
  if (e > now) e = now
  return { type, start: s, end: e, days: type === 'day' && v ? v : null }
}

/** The period the page query describes (no dates = All time). */
export function periodFromQuery(q: PageQuery, start: Date, now = new Date()): Period {
  if (!q.dateType || q.dateType === 'all' || !q.dateFrom) return { type: 'all', start, end: now, days: null }
  const s = new Date(q.dateFrom)
  const e = q.dateTo ? new Date(q.dateTo) : now
  if (!Number.isFinite(s.getTime())) return { type: 'all', start, end: now, days: null }
  const days = q.dateType === 'day' ? Math.round((startOfDay(now).getTime() - startOfDay(s).getTime()) / DAY) : null
  return { type: q.dateType, start: s, end: Number.isFinite(e.getTime()) ? e : now, days }
}

export interface PeriodItem {
  key: string
  /** i18n key + values, or a literal (years, month names). */
  label: { key: string; values?: Record<string, unknown> } | { text: string }
  divider?: boolean
  pick: { type: PeriodType; value: number | null } | 'custom'
}

const DAY_STEPS = [7, 30, 90, 180, 365] as const

/** PeriodDatepicker.datesList: what the dropdown offers for this account's age. */
export function periodItems(start: Date, now: Date, intl: string): PeriodItem[] {
  const ageDays = Math.floor((now.getTime() - start.getTime()) / DAY)
  const joinedLastDay = now.getTime() - start.getTime() <= DAY
  const items: PeriodItem[] = [
    { key: 'custom', label: { key: 'period.custom' }, pick: 'custom' },
    { key: '24', label: { key: 'period.last24' }, pick: { type: '24', value: null }, divider: true },
  ]
  const hide = [joinedLastDay, ageDays <= 7, ageDays <= 30, ageDays <= 90, ageDays <= 180]
  DAY_STEPS.forEach((n, i) => {
    if (!hide[i]) items.push({ key: `day-${n}`, label: { key: 'period.lastDays', values: { days: n } }, pick: { type: 'day', value: n } })
  })
  const year = now.getFullYear()
  const years = start.getFullYear() < year ? [year, year - 1] : [year]
  years.forEach((y, i) => items.push({ key: `year-${y}`, label: { text: String(y) }, pick: { type: 'year', value: y }, divider: i === 0 }))
  items.push({ key: 'all', label: { key: 'period.allTime' }, pick: { type: 'all', value: null } })
  monthsBack(start, now).forEach((m, i) =>
    items.push({
      key: `month-${i}`,
      label: { text: new Date(m.getFullYear(), m.getMonth(), 1).toLocaleString(intl, { month: 'long' }) },
      pick: { type: 'month', value: i },
      divider: i === 0,
    }),
  )
  return items
}

/** The last months back to the join month, at most six (OnlyFans' NO). */
function monthsBack(start: Date, now: Date): Date[] {
  const diff = (now.getFullYear() - start.getFullYear()) * 12 + now.getMonth() - start.getMonth()
  const n = Math.min(Math.max(diff, 0) + 1, 6)
  return Array.from({ length: n }, (_, i) => new Date(now.getFullYear(), now.getMonth() - i, 1))
}

/** "Oct 01, 2026" (default) or "9:05 am, Oct 01" (custom / last 24 hours). */
export function periodDates(p: Period, intl: string): { from: string; to: string } {
  const date = (d: Date, year = true) => {
    const mon = d.toLocaleString(intl, { month: 'short' }).replace('.', '')
    return `${mon} ${String(d.getDate()).padStart(2, '0')}${year ? `, ${d.getFullYear()}` : ''}`
  }
  const time = (d: Date) => `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() < 12 ? 'am' : 'pm'}`
  if (p.type === 'custom' || p.type === '24') {
    const sameYear = p.start.getFullYear() === p.end.getFullYear()
    const fromYear = p.type === 'custom' && !sameYear
    return { from: `${time(p.start)}, ${date(p.start, fromYear)}`, to: `${time(p.end)}, ${date(p.end)}` }
  }
  return { from: date(p.start), to: date(p.end) }
}

/** "+03:00" for "local time UTC +03:00". */
export function utcOffset(now = new Date()): string {
  const off = -now.getTimezoneOffset()
  const p = (n: number) => String(Math.trunc(Math.abs(n))).padStart(2, '0')
  return `${off >= 0 ? '+' : '-'}${p(off / 60)}:${p(off % 60)}`
}

/** What PeriodDatepicker.period titles the current choice. */
export function periodTitle(p: Period, intl: string): { key: string; values?: Record<string, unknown> } | { text: string } {
  if (p.type === 'custom') return { key: 'period.custom' }
  if (p.type === '24') return { key: 'period.last24' }
  if (p.type === 'all') return { key: 'period.allTime' }
  if (p.type === 'year') return { text: String(p.start.getFullYear()) }
  if (p.type === 'month') return { text: p.start.toLocaleString(intl, { month: 'long' }) }
  const days = p.days || Math.round((p.end.getTime() - p.start.getTime()) / DAY) || 7
  return { key: 'period.lastDays', values: { days: DAY_STEPS.find((n) => n >= days) ?? 365 } }
}
